import { CircularProgress } from "@mui/material";
import { useErrorMessage } from "@poseidon/ui/app/toasts/useErrorMessage";
import DataGrid from "@poseidon/ui/components/DataGrid";
import { DataGridContainer } from "@poseidon/ui/components/DataGridContainer";
import { usePermissions } from "@poseidon/ui/components";
import { getColumnDefinition } from "./columns";
import { useUsers } from "./useUsers";
import { useMakeAdmin } from "./useMakeAdmin";
import { useRevokeAdmin } from "./useRevokeAdmin";
import { useUpdateAdminPermissions } from "./useUpdateAdminPermissions";
import { AdminPermissionsDialog } from "./AdminPermissionsDialog";
import { useToastMessage } from "@poseidon/ui/app/toasts/useToastMessage";
import { useCallback, useState } from "react";

export default function ListSailors() {
  const { users, loading, error } = useUsers();
  const { setError } = useErrorMessage();
  const { setToast } = useToastMessage();
  const { canManage } = usePermissions();
  const makeAdminFunc = useMakeAdmin();
  const revokeAdminFunc = useRevokeAdmin();
  const updatePermissionsFunc = useUpdateAdminPermissions();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState(null); // "make" or "edit"
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  const handleMakeAdmin = useCallback((userId) => {
    setSelectedUserId(userId);
    setSelectedPermissions([]);
    setDialogMode("make");
    setDialogOpen(true);
  }, []);

  const handleEditPermissions = useCallback((userId, currentPermissions) => {
    setSelectedUserId(userId);
    setSelectedPermissions(currentPermissions);
    setDialogMode("edit");
    setDialogOpen(true);
  }, []);

  const handleDialogConfirm = useCallback(
    async (permissions) => {
      setDialogOpen(false);
      if (dialogMode === "make") {
        await makeAdminFunc.run(selectedUserId, permissions);
        if (makeAdminFunc.error) {
          return setError(`Unable to Make Admin: ${makeAdminFunc.error}`);
        }
        setToast("Promoted user to Admin");
      } else if (dialogMode === "edit") {
        await updatePermissionsFunc.run(selectedUserId, permissions);
        if (updatePermissionsFunc.error) {
          return setError(
            `Unable to update permissions: ${updatePermissionsFunc.error}`
          );
        }
        setToast("Updated admin permissions");
      }
    },
    [
      dialogMode,
      selectedUserId,
      makeAdminFunc,
      updatePermissionsFunc,
      setError,
      setToast,
    ]
  );

  const handleRevokeAdmin = useCallback(
    async (userId) => {
      await revokeAdminFunc.run(userId);
      if (revokeAdminFunc.error) {
        return setError(`Unable to Revoke Admin: ${revokeAdminFunc.error}`);
      }
      setToast("Revoked Admin for user");
    },
    [revokeAdminFunc, setError, setToast]
  );

  if (error) {
    setError(error);
  }

  if (loading || !users) {
    return (
      <CircularProgress sx={{ alignSelf: "center", justifySelf: "center" }} />
    );
  }

  return (
    <DataGridContainer>
      <DataGrid
        columns={getColumnDefinition({
          handleMakeAdmin,
          handleRevokeAdmin,
          handleEditPermissions,
          canManage,
        })}
        rows={users}
        initialState={{
          sorting: {
            sortModel: [{ field: "createdAt", sort: "desc" }],
          },
        }}
      />
      <AdminPermissionsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleDialogConfirm}
        title={
          dialogMode === "make"
            ? "Set Permissions for New Admin"
            : "Edit Admin Permissions"
        }
        initialPermissions={selectedPermissions}
      />
    </DataGridContainer>
  );
}
